import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../../../context/CurrencyContext';
import { formatOrderReference } from '../../../../../utils/formatOrderReference';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

export default function RefundsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { authorizedFetch } = useAuth();
  const { formatPriceFrom } = useCurrency();
  const [rows, setRows] = useState<any[]>([]);
  const [summary, setSummary] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const globalAuthorizedFetch = useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      // Refunds page is intentionally global (not department-scoped).
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (status) q.set('status', status);
      const [a, b] = await Promise.all([
        globalAuthorizedFetch(`${API_BASE}/finance/refunds?${q.toString()}`),
        globalAuthorizedFetch(`${API_BASE}/finance/refunds/summary`),
      ]);
      const aj = await a.json();
      const bj = await b.json();
      setRows(Array.isArray(aj?.items) ? aj.items : []);
      setSummary(Array.isArray(bj?.rows) ? bj.rows : []);
    } finally {
      setLoading(false);
    }
  }, [globalAuthorizedFetch, status]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={`${location.pathname}${location.search}`}
    >
      <main className="workspace-page">
        <div className="flex items-center justify-between">
          <h1 className="workspace-page-title">
            {t('refunds_title', { defaultValue: 'Refunds' })}
          </h1>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">{t('all_statuses', { defaultValue: 'All statuses' })}</option>
            <option value="PROCESSING">{t('status.processing', { defaultValue: 'Processing' })}</option>
            <option value="DONE">{t('status.done', { defaultValue: 'Done' })}</option>
            <option value="FAILED">{t('status.failed', { defaultValue: 'Failed' })}</option>
          </select>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          {summary.map((s) => (
            <div key={s.currency} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-xs uppercase text-slate-500">{s.currency}</div>
              <div className="mt-1 text-lg font-semibold text-slate-900">
                {formatPriceFrom((Number(s.amountMinor || 0) + Number(s.feeMinor || 0)) / 100, s.currency)}
              </div>
              <div className="text-xs text-slate-500">
                {s.doneCount}/{s.count}{' '}
                {t('common.done', { defaultValue: 'done' })}
              </div>
            </div>
          ))}
        </div>
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-4 py-3">{t('common.date', { defaultValue: 'Date' })}</th>
                <th className="px-4 py-3">{t('common.order', { defaultValue: 'Order' })}</th>
                <th className="px-4 py-3">{t('common.amount', { defaultValue: 'Amount' })}</th>
                <th className="px-4 py-3">{t('common.status', { defaultValue: 'Status' })}</th>
                <th className="px-4 py-3">
                  {t('gateway_refund', { defaultValue: 'Gateway refund' })}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-4 py-6 text-slate-500" colSpan={5}>
                    {t('common.loading', { defaultValue: 'Loading...' })}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-slate-500" colSpan={5}>
                    {t('refunds_empty', { defaultValue: 'No refunds found.' })}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-4 py-3">{new Date(r.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-3 font-mono text-xs">{formatOrderReference(r.orderId)}</td>
                    <td className="px-4 py-3">{formatPriceFrom(Number(r.amountMinor || 0) / 100, r.currency || 'USD')}</td>
                    <td className="px-4 py-3">{r.status}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.stripeRefundId || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>
    </MainLayout>
  );
}

